import { useState, useCallback } from 'react'
import { useGitHubToken } from './useGitHubToken'
import { getApiBaseUrl } from '../lib/apiBaseUrl'
import { messageFromApiErrorBody } from '../lib/parseApiError'

export function useGitHubTokenAPI() {
  const { token, setToken: setTokenLocal, clearToken } = useGitHubToken()
  const [isValidating, setIsValidating] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const validateAndSetToken = useCallback(async (tokenToValidate: string, username?: string) => {
    setIsValidating(true)
    setValidationError(null)

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          token: tokenToValidate,
          username: username
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const msg = messageFromApiErrorBody(errorData)
        throw new Error(msg || `HTTP ${response.status}: ${response.statusText}`)
      }

      const result = await response.json()
      
      if (result.valid) {
        // Sauvegarder le token localement après validation
        setTokenLocal(tokenToValidate)
        return result
      } else {
        throw new Error(result.message || 'Token invalide')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de la validation du token'
      setValidationError(errorMessage)
      throw error
    } finally {
      setIsValidating(false)
    }
  }, [setTokenLocal])

  const validateToken = useCallback(async (tokenToValidate: string) => {
    setIsValidating(true)
    setValidationError(null)

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/token/validate`, {
        method: 'GET',
        headers: {
          'X-GitHub-Token': tokenToValidate,
          'Accept': 'application/json',
        },
      })

      const result = await response.json()
      return result.valid
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : 'Erreur de validation')
      return false
    } finally {
      setIsValidating(false)
    }
  }, [])

  const removeToken = useCallback(async () => {
    if (token) {
      try {
        await fetch(`${getApiBaseUrl()}/api/auth/token`, {
          method: 'DELETE',
          headers: {
            'X-GitHub-Token': token,
          },
        })
      } catch (error) {
        console.error('Error removing token from server:', error)
      }
    }
    clearToken()
  }, [token, clearToken])

  return {
    token,
    setToken: validateAndSetToken,
    removeToken,
    validateToken,
    isValidating,
    validationError,
    hasToken: !!token
  }
}

